import { Link } from "react-router-dom";
import { Button } from "@/components/Button";

interface ForbiddenStateProps {
  destination: string | null;
  onSignOut: () => void;
}

/**
 * Query-free state for an authenticated admin who cannot open a route.
 * It intentionally stays outside `AdminLayout` so a denied page cannot mount
 * layout or page queries while explaining the authorization outcome.
 * @param root0 - Component props.
 * @param root0.destination - First protected route the current admin may open.
 * @param root0.onSignOut - Optimistic current-session sign-out action.
 * @returns The static access-unavailable state.
 */
export function ForbiddenState({
  destination,
  onSignOut,
}: ForbiddenStateProps): React.ReactElement {
  return (
    <main className="bg-background flex min-h-screen items-center justify-center px-4 py-6">
      <section
        aria-labelledby="forbidden-heading"
        className="border-hairline bg-surface w-full max-w-lg rounded-md border p-6 text-center"
      >
        <h1 id="forbidden-heading" className="text-foreground text-xl font-semibold">
          Access unavailable
        </h1>
        <p className="text-foreground-muted mt-2 text-sm">
          This administrator account does not have permission to open this page.
        </p>
        {destination ? (
          <Link
            to={destination}
            className="bg-primary-600 hover:bg-primary-700 focus-visible:ring-primary-500 mt-6 inline-flex min-h-11 items-center justify-center rounded-sm px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            Go to an available page
          </Link>
        ) : (
          <div className="mt-4">
            <p className="text-foreground-muted text-sm">
              Contact another platform administrator if you need access.
            </p>
            <Button variant="secondary" onClick={onSignOut} className="mt-6">
              Sign Out
            </Button>
          </div>
        )}
      </section>
    </main>
  );
}
